import React from 'react';
import { motion } from 'framer-motion';
import SEOHead from '../SEOHead';
import Footer from '../sections/Footer';
import { Link } from 'react-router-dom';

const lastUpdated = 'June 4, 2026';

const sections = [
  { t: 'Who We Are', b: [
    'ManuscriptSmith ("we", "us") provides an AI-assisted writing studio. This policy explains what personal data we collect, how we use it, and your rights. For data you put into the Service (your manuscripts and Voice Profile), we act as a processor on your behalf; for account and billing data, we act as a controller.'
  ]},
  { t: 'Data We Collect', b: [
    'Account data: name, email, password (hashed), and settings. Billing data: subscription status and payment metadata (card details are handled by our payment processor and are not stored by us). Content data: manuscripts, prompts, notes, writing samples, and your Author Voice DNA profile. Usage data: device, browser, log, and feature-usage information collected to operate and secure the Service.'
  ]},
  { t: 'How We Use Your Data', b: [
    'To provide the Service (render your editor, generate suggestions in your voice, run manuscript preflight checks, create exports), to process payments, to provide support, to secure the Service and prevent abuse, and to communicate important account or service notices. We do not sell your personal data.'
  ]},
  { t: 'AI & Your Content — No Model Training', b: [
    'We do not use your manuscripts, prompts, outputs, or Voice Profile to train, fine-tune, or improve general AI models. When we use authorized AI model providers to generate your output, your inputs are sent only to produce that output, under agreements that prohibit training on your content. We may use de-identified, aggregated telemetry and abuse-prevention logs to keep the Service safe and reliable.'
  ]},
  { t: 'Your Author Voice DNA', b: [
    'Your writing-style profile is private to your account. It is not shared with other users, not used to generate output for anyone else, and excluded from model training. You can view, edit, export, or delete it at any time.'
  ]},
  { t: 'Cookies & Similar Technologies', b: [
    'We use essential cookies to keep you signed in and the Service working, and (only with your consent) analytics cookies to understand usage. You can manage your choice through our cookie banner and your browser settings. See our Cookies Policy for details.'
  ]},
  { t: 'Subprocessors We Share With', b: [
    'We share data only with providers needed to run the Service: authorized AI model providers to generate output; a payment processor for billing; and cloud infrastructure providers for hosting, storage, and delivery. Each is bound by data-processing terms. A current subprocessor list is available on request.'
  ]},
  { t: 'Plugins & Add-ons', b: [
    'When you use optional plugins (such as style analysis, manuscript diagnostics, cover generation, or export tools), those plugins may process your manuscript content or metadata to perform their function. Plugin data is handled under the same rules as core Service data — it is not used for AI model training and is not shared beyond what the plugin needs to operate. You can manage which plugins have access to your content from your account settings.'
  ]},
  { t: 'Data Retention & Deletion', b: [
    'We keep your content and account data while your account is active and as needed to provide the Service. When you delete content or your account, we remove it from active systems promptly and from routine backups within a limited window. We may retain minimal records where required by law.'
  ]},
  { t: 'Security', b: [
    'We protect your data with encryption in transit and at rest, access controls, and routine security practices. No system is perfectly secure, but your manuscripts and Voice Profile are treated as confidential.'
  ]},
  { t: 'Your Rights (GDPR & CCPA)', b: [
    'Depending on where you live, you may have the right to access, correct, export, delete, or restrict processing of your personal data, to object to certain uses, and to withdraw consent. California residents may request disclosure of data practices and opt out of "sale" or "sharing" (we do neither). To exercise any right, email privacy@manuscriptsmith.com. We will not discriminate against you for exercising your rights.'
  ]},
  { t: 'International Transfers', b: [
    'We are based in the United States and may process data there and in other countries where our providers operate, using appropriate safeguards (such as Standard Contractual Clauses) where required.'
  ]},
  { t: 'Children', b: [
    'The Service is not directed to children under 16, and we do not knowingly collect their personal data. If you believe a child has provided us data, contact us and we will delete it.'
  ]},
  { t: 'Changes & Contact', b: [
    'We may update this policy and will post the new version with an updated date; we will notify you of material changes where practicable. Questions or requests: privacy@manuscriptsmith.com.'
  ]}
];

const PrivacyPolicy = () => (
  <div className="min-h-screen bg-parchment-50">
    <SEOHead
      title="Privacy Policy - ManuscriptSmith"
      description="How ManuscriptSmith protects your manuscripts, Author Voice DNA, and personal data — including our promise never to train AI on your work, plus your GDPR and CCPA rights."
      canonicalUrl="https://manuscriptsmith.com/privacy"
    />
    <nav className="bg-white shadow-sm border-b sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">MS</span>
            </div>
            <span className="text-xl font-bold text-gray-900">ManuscriptSmith</span>
          </Link>
          <Link to="/" className="text-sm font-medium text-gray-600 hover:text-primary-600">Return Home</Link>
        </div>
      </div>
    </nav>

    <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
        <div className="border-b border-gray-200 pb-8 mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-gray-500">Last Updated: {lastUpdated}</p>
        </div>
        <div className="max-w-none text-gray-700 space-y-8 leading-relaxed">
          {sections.map((s, i) => (
            <section key={s.t}>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{i + 1}. {s.t}</h2>
              {s.b.map((p, j) => <p key={j} className="mb-3">{p}</p>)}
            </section>
          ))}
        </div>
      </motion.div>
    </main>
    <Footer />
  </div>
);

export default PrivacyPolicy;
